import { GoogleDriveFilePicker } from '@/components/ChatV2/GoogleDriveFilePicker'
import { trackMetaPixelEvent } from '@/components/MetaPixel'
import PersonalizationPanel from '@/components/PersonalizationPanel/PersonalizationPanel'
import { AttachmentButton } from '@/components/ui/AttachmentButton'
import { useAuthModal } from '@/hooks/useAuthModal'
import { useFileUpload } from '@/hooks/useFileUpload'
import { useGoogleDriveFileHandler } from '@/hooks/useGoogleDriveFileHandler'
import { useGoogleDriveIntegration } from '@/hooks/useGoogleDriveIntegration'
import { compareScreenSizes, useScreenSize } from '@/hooks/useScreenSize'
import { useTooltip } from '@/hooks/useTooltip'
import LoadingCircleIcon from '@/icons/LoadingCircleIcon'
import { analytics } from '@/lib/analytics'
import { getCopilotService } from '@/lib/features/copilotv2/CopilotService'
import { setInitialMessage } from '@/lib/features/copilotv2/copilotInitialMessageStorage'
import {
  useSessionAwareUserQuery,
  useSessionAwareUserQuotaQuery,
} from '@/lib/features/user/userApi'
import { useAppDispatch } from '@/lib/hooks'
import { FileWithUploadMetadata } from '@/lib/type'
import { cn } from '@/lib/utils'
import { IconArrowNarrowUp } from '@tabler/icons-react'

import { usePricingModal } from '@/hooks/usePricingModal'
import {
  CtrlClickLink,
  handleSpaceInLink,
  handleSpaceOutsideLink,
  insertPlainTextWithLinks,
} from '@/lib/editor/link-utils'
import { Editor, EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { motion } from 'framer-motion'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FiCloud, FiSettings } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { AgentType } from 'sprites-types/api/v2/copilot'

const Shortcut: React.FC<{
  children: React.ReactNode
  onClick: () => void
}> = ({ children, onClick }) => {
  return (
    <button
      className="!font-space-grotesk flex items-center gap-1 rounded-[8px] border border-[#DCDCDC] bg-white px-2 py-0.5 text-[14px] font-light transition-colors duration-200 hover:bg-[#DCDCDC]"
      onClick={onClick}
    >
      {children} <IconArrowNarrowUp size={14} className="mt-0.5" />
    </button>
  )
}

const SubmitButton: React.FC<{
  onClick: () => void
  disabled?: boolean
  isLoading?: boolean
}> = ({ onClick, disabled = false, isLoading = false }) => {
  return (
    <button
      className="flex h-[24px] w-[24px] items-center justify-center rounded-[8px] bg-black text-white transition-colors duration-200 hover:bg-zinc-800"
      onClick={onClick}
      disabled={disabled || isLoading}
    >
      {isLoading ? (
        <LoadingCircleIcon className="text-white" width="18" height="18" />
      ) : (
        <IconArrowNarrowUp size={20} />
      )}
    </button>
  )
}

const TooltipButton: React.FC<{
  icon: React.ReactNode
  label: string
  onClick: React.MouseEventHandler<HTMLButtonElement>
  children?: React.ReactNode
}> = ({ icon, label, onClick, children }) => {
  const { showTooltip, tooltipPosition, buttonRef, showTooltipWithDelay, hideTooltip } = useTooltip(
    { position: 'top' }
  )

  return (
    <>
      <button
        ref={buttonRef}
        className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-[#E0E0E0] focus:bg-[#E0E0E0]"
        aria-label={label}
        onClick={onClick}
        onMouseEnter={showTooltipWithDelay}
        onMouseLeave={hideTooltip}
      >
        {icon}
        {children}
      </button>
      {showTooltip &&
        typeof window !== 'undefined' &&
        createPortal(
          <div
            className="font-hanken-grotesk pointer-events-none fixed z-[9999] -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
          >
            {label}
          </div>,
          document.body
        )}
    </>
  )
}

export interface LandingInputProps {
  isScrolled: boolean
  isHidden?: boolean
  isFixed?: boolean
  titleText?: string
  agentType?: AgentType
}

const LandingInput: React.FC<LandingInputProps> = ({
  isScrolled,
  isHidden = false,
  isFixed = true,
  titleText = 'What do you want to automate with AI today?',
  agentType,
}) => {
  const textRef = useRef('')
  const textInputRef = useRef<HTMLDivElement>(null)
  const isNavigatingRef = useRef(false)
  const router = useRouter()
  const dispatch = useAppDispatch()
  const { data: userData, isLoading: userLoading, error: userError } = useSessionAwareUserQuery()
  const {
    data: quota,
    isLoading: quotaLoading,
    error: quotaError,
  } = useSessionAwareUserQuotaQuery()

  const { isOpen: isAuthModalOpen } = useAuthModal()
  const screenSize = useScreenSize()
  const isMobile = compareScreenSizes(screenSize, 'md') < 0
  const [isInputEmpty, setIsInputEmpty] = useState(true)
  const [isFocused, setIsFocused] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState<boolean>(false)
  const { setOpen: openPricingModal } = usePricingModal()
  const [showPersonalization, setShowPersonalization] = useState(false)

  // Use shared file upload hook
  const {
    files,
    fileObjectUrls,
    addFiles,
    removeFile,
    hasUploadingFiles,
    setFiles,
    setFileObjectUrls,
  } = useFileUpload()

  // Google Drive integration
  const {
    showGoogleDrivePicker,
    googleDriveAccountId,
    handleGoogleDriveClick,
    isGoogleDriveConnected,
    isCheckingGoogleDrive,
    setShowGoogleDrivePicker,
  } = useGoogleDriveIntegration({ userData })

  // Tiptap editor with list support
  const editor = useEditor({
    immediatelyRender: false, // Fix SSR hydration mismatch
    extensions: [
      StarterKit.configure({
        // Enable list support for markdown-style lists
        bulletList: {
          HTMLAttributes: {
            class: 'list-disc space-y-1',
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: 'list-decimal space-y-1',
          },
        },
        listItem: {
          HTMLAttributes: {
            class: 'leading-relaxed',
          },
        },

        // Keep other useful features
        blockquote: {
          HTMLAttributes: {
            class: 'border-l-4 border-gray-300 pl-4 italic',
          },
        },
        code: {
          HTMLAttributes: {
            class: 'bg-gray-100 px-1 py-0.5 rounded text-sm font-mono',
          },
        },
        codeBlock: {
          HTMLAttributes: {
            class: 'bg-gray-100 p-3 rounded font-mono text-sm',
          },
        },

        // Disable features not needed for prompt input
        heading: false,
        horizontalRule: false,
        strike: false,
        bold: false,
        italic: false,
      }),
      CtrlClickLink.configure({
        autolink: false,
        openOnClick: false,
        linkOnPaste: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
          class: 'text-blue-600 underline hover:text-blue-800',
        },
      }).extend({
        addKeyboardShortcuts() {
          return {
            Space: ({ editor }: { editor: Editor }) => {
              if (editor.isActive('link')) {
                return handleSpaceInLink(editor)
              }
              return handleSpaceOutsideLink(editor)
            },
          }
        },
      }),
    ],
    content: '',
    editable: true,
    onUpdate: ({ editor }) => {
      const text = editor.getText()
      // Check if editor is completely empty (no text, no nodes, no whitespace)
      const isEmpty = text.trim() === '' && editor.state.doc.content.size <= 2 // 2 is the minimum size for an empty document
      setIsInputEmpty(isEmpty)
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    onCreate: () => {
      setIsInputEmpty(true)
    },
    editorProps: {
      handlePaste: (view, event) => {
        // Always paste as plain text, stripping all formatting including links
        const plainText = event.clipboardData?.getData('text/plain')
        if (plainText) {
          event.preventDefault()
          insertPlainTextWithLinks(view, plainText)
          return true
        }
        return false
      },
      handleKeyDown: (view, event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault()
          const text = editor?.getText() || ''
          if (text.trim() || files.length > 0) {
            handleInputSubmit(text.trim())
          }
          return true
        }
        if (event.key === 'Tab' && !event.shiftKey && isInputEmpty && !isAuthModalOpen) {
          event.preventDefault()
          analytics.landingPage.promptboxTabPressed()
          performTabAutocomplete()
          return true
        }
        return false
      },
    },
  })

  const { handleGoogleDriveSendMessage } = useGoogleDriveFileHandler({
    setFiles,
    addFiles,
    setFileObjectUrls,
  })

  // Whitelisted agents that match backend configuration
  const WHITELISTED_AGENTS: AgentType[] = [AgentType.META_ADS, AgentType.GOOGLE_ADS, AgentType.SEO]

  // Agent-specific prompts with [button label, full prompt] format
  const getAgentPrompts = (): Map<AgentType, [string, string][]> => {
    const prompts = new Map<AgentType, [string, string][]>()

    prompts.set(AgentType.SEO, [
      ['SEO Audit', 'Audit my website for technical SEO issues'],
      ['Content Strategy', 'Generate a content strategy to improve my rankings'],
      ['Competitor Keywords', 'Find keyword opportunities my competitors are ranking for'],
    ])

    prompts.set(AgentType.META_ADS, [
      ['Meta Campaign', 'Create a Facebook and Instagram ads campaign for my product'],
      ['Audience Research', 'Research and build custom audiences for Meta Ads targeting'],
      ['Creative Testing', 'Set up A/B testing for Meta advertising creatives and copy'],
    ])

    prompts.set(AgentType.GOOGLE_ADS, [
      ['Google Campaign', 'Create a comprehensive Google Ads campaign with optimized keywords'],
      ['Keyword Research', 'Research high-converting keywords and build Google Ads campaigns'],
      ['Performance Max', 'Set up and optimize Google Performance Max campaigns'],
    ])

    // Filter to only include whitelisted agents
    const filteredPrompts = new Map<AgentType, [string, string][]>()
    for (const agentType of WHITELISTED_AGENTS) {
      const agentPrompts = prompts.get(agentType)
      if (agentPrompts) {
        filteredPrompts.set(agentType, agentPrompts)
      }
    }

    return filteredPrompts
  }

  const AGENT_PROMPTS = getAgentPrompts()

  // Get shortcuts based on selected agent type - show 3 prompts
  const shortcuts: [string, string][] =
    agentType && AGENT_PROMPTS.has(agentType)
      ? AGENT_PROMPTS.get(agentType)!.slice(0, 3)
      : [
          ['Meta Campaign', 'Create a Facebook and Instagram ads campaign for my product'],
          ['Audience Research', 'Research and build custom audiences for Meta Ads targeting'],
          ['Creative Testing', 'Set up A/B testing for Meta advertising creatives and copy'],
        ]

  const handleShortcutClick = useCallback(
    (text: string, title: string) => {
      if (editor) {
        editor.commands.setContent(text)
        editor.commands.focus()
        // Move cursor to end
        editor.commands.setTextSelection(editor.state.doc.content.size)
      }
      textRef.current = text
      setIsInputEmpty(false)

      analytics.landingPage.promptboxOptionClicked(title)
      // Track prompt box option click in Meta Pixel
      trackMetaPixelEvent('Search', {
        search_string: title,
        content_type: 'prompt_template',
      })
    },
    [editor]
  )

  // handleKeyDown is now handled in editor configuration

  const performTabAutocomplete = useCallback(() => {
    const placeholderText = 'Find every lead from a conference website and give me a CSV file'
    if (editor) {
      editor.commands.setContent(placeholderText)
      editor.commands.focus()
      // Move cursor to end
      editor.commands.setTextSelection(editor.state.doc.content.size)
    }
    setIsInputEmpty(false)
  }, [editor])

  const handlePreferencesClick = () => {
    setShowPersonalization(true)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    if (selectedFiles.length > 0) {
      addFiles(selectedFiles)
      setIsInputEmpty((editor?.getText() || '').trim() === '')
    }
  }

  // handleRemoveFile replaced by removeFile function above

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const droppedFiles = Array.from(e.dataTransfer.files)
    if (droppedFiles.length > 0) {
      addFiles(droppedFiles)
      setIsInputEmpty((editor?.getText() || '').trim() === '')
    }
  }

  const handleInputSubmit = useCallback(
    async (text: string) => {
      // Don't process if already navigating
      if (isNavigatingRef.current) {
        return
      }

      // Filter out unsupported files
      const supportedFiles = files.filter((file) => !(file as FileWithUploadMetadata).isUnsupported)

      try {
        if (!userData || !userData.workspace?.id) {
          // Show pricing modal
          openPricingModal()
          return
        }

        // Show loading but don't clear input yet
        setIsLoading(true)

        // Flag that we're in navigation process - input should not be cleared
        isNavigatingRef.current = true

        // Clean text if needed
        const textToUse = text.includes('do-profiling') ? text.replace('do-profiling', '') : text

        const doProfiling = text.includes('do-profiling')

        // If files are present, use already-uploaded URLs or upload if needed
        let fileLinks: { url: string; filename: string; ext: string }[] | undefined
        if (supportedFiles.length > 0) {
          const uploadPromises = supportedFiles.map(async (file) => {
            const fileWithMeta = file as FileWithUploadMetadata
            // If file was already uploaded, use existing URL
            if (fileWithMeta.uploadedUrl) {
              return {
                url: fileWithMeta.uploadedUrl,
                filename: file.name,
                ext: file.name.split('.').pop() || '',
              }
            }
            // Otherwise upload now (fallback for any files that weren't pre-uploaded)
            const formData = new FormData()
            formData.append('file', file)
            const res = await fetch(
              `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/services/upload-file`,
              { method: 'POST', body: formData, credentials: 'include' }
            )
            if (!res.ok) {
              throw new Error(
                `File upload failed for ${file.name}: ${res.status} ${res.statusText}`
              )
            }
            const data = await res.json()
            if (!data.url) {
              throw new Error(`No file URL returned for ${file.name}`)
            }
            return { url: data.url, filename: file.name, ext: file.name.split('.').pop() || '' }
          })
          fileLinks = await Promise.all(uploadPromises)
        }

        // Create a Copilot Session first to get the session ID
        const copilotService = getCopilotService()
        // Determine source based on current pathname
        const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
        const source = pathname === '/dashboard' ? 'dashboard_prompt_box' : 'landing_page'
        const sessionResp = await copilotService.createSession({
          workspaceId: userData.workspace.id,
          source,
          agentType,
        })

        // Store the message in storage for the session to pick up
        await setInitialMessage(sessionResp.session.id, {
          message: textToUse,
          options: { doProfiling },
          fileLinks,
          agentType,
        })

        // Track prompt submission in Meta Pixel with more detailed information
        trackMetaPixelEvent('AppCreation', {
          search_string: textToUse,
          content_type: 'custom_prompt',
          prompt_length: textToUse.length,
          is_profiling: doProfiling,
          has_file: files.length > 0,
          file_count: files.length,
          file_types: files.map((f) => f.type),
          user_id: userData.user?.id,
          workspace_id: userData.workspace.id,
        })

        // Analytics
        analytics.landingPage.promptboxSubmitted(textToUse)

        // Begin navigation with agentType as query parameter for extra safety
        const url = `/copilot/${sessionResp.session.id}${agentType ? `?agentType=${agentType}` : ''}`
        router.push(url)
      } catch (error) {
        console.error('Error submitting text:', error)
        toast.error('Failed to create agent')

        // Clear the loading state and flags
        setIsLoading(false)
      } finally {
        isNavigatingRef.current = false
      }
    },
    [router, userData, dispatch, quota, files, openPricingModal, agentType]
  )

  // Input handling is now done by Tiptap editor

  // Global keyboard event for Tab when not focused
  useEffect(() => {
    if (isAuthModalOpen) return

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab' && !e.shiftKey && !isFocused && isInputEmpty && !isLoading) {
        e.preventDefault()

        // Track tab press for analytics
        analytics.landingPage.promptboxTabPressed()

        performTabAutocomplete()
      }
    }

    document.addEventListener('keydown', handleGlobalKeyDown)
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown)
    }
  }, [isAuthModalOpen, isFocused, isInputEmpty, isLoading, performTabAutocomplete])

  // Show loading state while fetching user data
  if (userLoading || quotaLoading) {
    return (
      <motion.div
        className={cn(
          'mx-auto flex w-[95%] max-w-[604px] flex-col items-center rounded-2xl p-4 shadow-lg backdrop-blur transition-colors duration-200',
          isFixed && 'fixed bottom-10 left-1/2 z-50 -translate-x-1/2',
          isScrolled ? 'bg-white/50' : 'bg-white',
          isHidden ? 'pointer-events-none select-none' : ''
        )}
        initial={false}
        animate={{
          opacity: isHidden ? 0 : 1,
        }}
        transition={{
          duration: 1.25,
          ease: 'easeOut',
        }}
      >
        <div className="flex items-center justify-center py-4">
          <LoadingCircleIcon className="h-6 w-6" />
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      </motion.div>
    )
  }

  // Show error state if there's an error loading user data
  if (userError || quotaError) {
    return (
      <motion.div
        className={cn(
          'mx-auto flex w-[95%] max-w-[604px] flex-col items-center rounded-2xl p-4 shadow-lg backdrop-blur transition-colors duration-200',
          isFixed && 'fixed bottom-10 left-1/2 z-50 -translate-x-1/2',
          isScrolled ? 'bg-white/50' : 'bg-white',
          isHidden ? 'pointer-events-none select-none' : ''
        )}
        initial={false}
        animate={{
          opacity: isHidden ? 0 : 1,
        }}
        transition={{
          duration: 1.25,
          ease: 'easeOut',
        }}
      >
        <div className="flex flex-col items-center justify-center py-4 text-red-600">
          <svg className="mb-2 h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-center text-sm">
            {userError ? 'Failed to load user data' : 'Failed to load quota data'}
          </span>
          <button
            className="mt-2 text-xs text-blue-600 underline"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </motion.div>
    )
  }

  return (
    <motion.div
      className={cn(
        'mx-auto flex w-[95%] max-w-[604px] flex-col items-center rounded-2xl p-4 shadow-lg backdrop-blur transition-colors duration-200',
        isFixed && 'fixed bottom-10 left-1/2 z-50 -translate-x-1/2',
        isScrolled ? 'bg-white/50' : 'bg-white',
        isHidden ? 'pointer-events-none select-none' : '',
        isDragOver ? 'border-blue-400 bg-blue-50' : ''
      )}
      initial={false}
      animate={{
        opacity: isHidden ? 0 : 1,
      }}
      transition={{
        duration: 1.25,
        ease: 'easeOut',
      }}
      ref={textInputRef}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <motion.p
        className={cn(
          '!font-space-grotesk',
          isMobile ? 'text-[16px] leading-tight' : 'text-[20px]'
        )}
        initial={false}
        animate={
          isMobile
            ? { height: 'auto', opacity: isScrolled ? 0 : 1 }
            : { height: isScrolled ? 0 : 35, opacity: isScrolled ? 0 : 1 }
        }
        style={isMobile ? { minHeight: 0 } : {}}
      >
        {titleText}
      </motion.p>

      {googleDriveAccountId && (
        <div className={cn('w-full', showGoogleDrivePicker && 'mb-2 mt-2')}>
          <GoogleDriveFilePicker
            filePickerInfo={{
              accountId: googleDriveAccountId,
              limit: 0,
            }}
            sendMessage={handleGoogleDriveSendMessage}
            onClose={() => setShowGoogleDrivePicker(false)}
            dropdownPosition="left"
            isVisible={showGoogleDrivePicker}
          />
        </div>
      )}

      {/* Files preview */}
      {files.length > 0 && (
        <div className="mt-2 flex max-w-full flex-col gap-2">
          {/* Files container - responsive flex layout */}
          <div className="flex flex-wrap gap-2">
            {files.map((file) => {
              const fileKey = `${file.name}-${file.size}-${file.lastModified}`
              const fileObjectUrl = fileObjectUrls.get(fileKey)
              const fileWithMeta = file as FileWithUploadMetadata
              const isDownloading = fileWithMeta.isDownloading === true
              const isUploading = fileWithMeta.isUploading === true
              const isUnsupported = fileWithMeta.isUnsupported === true
              const uploadProgress = fileWithMeta.uploadProgress || 0

              return (
                <div
                  key={fileKey}
                  className={cn(
                    'relative flex min-w-0 flex-1 basis-full items-center gap-2 rounded p-2 shadow-sm sm:basis-auto',
                    isUnsupported ? 'bg-red-50 opacity-75' : 'bg-white'
                  )}
                >
                  {/* Upload progress bar */}
                  {isUploading && uploadProgress < 100 && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 overflow-hidden rounded-b bg-gray-100">
                      <div
                        className="h-full bg-blue-500 transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  )}

                  {file.type.startsWith('image/') ? (
                    fileObjectUrl && (
                      <Image
                        src={fileObjectUrl}
                        alt={file.name}
                        className="h-10 w-10 rounded object-cover"
                        width={40}
                        height={40}
                      />
                    )
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-200">
                      <span className="text-xs text-gray-600">
                        {file.name.split('.').pop()?.toUpperCase() || 'FILE'}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-gray-800">{file.name}</div>
                    <div className="flex items-center gap-1 truncate text-xs">
                      {isUnsupported ? (
                        <span className="font-medium text-red-600">
                          {fileWithMeta.unsupportedReason || 'Unsupported file type'}
                        </span>
                      ) : isDownloading ? (
                        <span className="text-gray-500">Downloading...</span>
                      ) : isUploading ? (
                        <span className="text-blue-600">Uploading {uploadProgress}%</span>
                      ) : (
                        <>
                          <FiCloud className="h-3 w-3 text-gray-500" />
                          <span className="text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
                          {fileWithMeta.uploadedUrl && (
                            <span className="ml-1 text-green-600">✓</span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="ml-2 text-gray-400 hover:text-red-500"
                    aria-label="Remove file"
                    onClick={() => removeFile(file)}
                    disabled={isDownloading || isUploading}
                  >
                    ×
                  </button>
                </div>
              )
            })}
          </div>
          {files.length > 1 && (
            <div className="text-center text-xs text-gray-500">
              {files.length} files selected (
              {(files.reduce((sum, f) => sum + f.size, 0) / 1024).toFixed(1)} KB total)
            </div>
          )}
        </div>
      )}

      <div className="!font-space-grotesk relative mt-2 w-full rounded-[8px] bg-[#f5f5f5]">
        <div className="relative h-full w-full">
          <div
            className="!font-space-grotesk w-full overflow-y-auto bg-transparent px-4 py-2 text-[16px] text-[#191919] outline-none"
            style={{
              borderColor: isFocused ? '#D1D5DB' : 'transparent',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderRadius: '8px',
            }}
          >
            <EditorContent
              editor={editor}
              className="!font-space-grotesk prose prose-sm w-full max-w-none resize-none bg-transparent text-[16px] leading-6 text-[#191919] outline-none [&_.ProseMirror_li]:my-1 [&_.ProseMirror_li]:pl-0 [&_.ProseMirror_li]:leading-relaxed [&_.ProseMirror_ol]:my-2 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-6 [&_.ProseMirror_p]:my-2 [&_.ProseMirror_ul]:my-2 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-6"
              spellCheck={true}
              style={{ minHeight: '24px', maxHeight: '192px' }}
            />
            {isInputEmpty && (
              <div className="!font-space-grotesk pointer-events-none absolute inset-0 flex select-none items-start overflow-hidden text-ellipsis px-4 py-4 text-[16px] font-light text-[#ABABAB]">
                Find every lead from a conference website and give me a CSV file
              </div>
            )}
          </div>
          {!isMobile && isInputEmpty && (
            <div className="!font-space-grotesk pointer-events-none absolute right-4 top-4 select-none">
              <span className="!font-space-grotesk rounded-md bg-[#E6E6E6] px-1 py-0.5 text-xs font-light text-[#ABABAB]">
                TAB
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="mt-2 flex w-full flex-wrap items-end justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {shortcuts.map(([title, text], index) => (
            <Shortcut key={index} onClick={() => handleShortcutClick(text, title)}>
              {title}
            </Shortcut>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <TooltipButton
            icon={<FiSettings className="text-[#757575]" />}
            label="Personalization"
            onClick={handlePreferencesClick}
          />
          <AttachmentButton
            onGoogleDriveClick={handleGoogleDriveClick}
            fileInputRef={fileInputRef}
            dropdownPosition="left-center"
            isGoogleDriveConnected={isGoogleDriveConnected}
            isCheckingGoogleDrive={isCheckingGoogleDrive}
          />
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            tabIndex={-1}
            accept="*"
            multiple
          />
          {(!isInputEmpty || files.length > 0) && (
            <SubmitButton
              onClick={() => handleInputSubmit(editor?.getText() || '')}
              disabled={isLoading || hasUploadingFiles}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>
      {/* Personalization Panel */}
      <PersonalizationPanel
        isOpen={showPersonalization}
        onClose={() => setShowPersonalization(false)}
      />
    </motion.div>
  )
}

export default LandingInput
